import { type StyledComponentProps, useStyledSystem } from "@hopper-ui/components";
import { clsx } from "clsx";
import type { CSSProperties, ComponentProps } from "react";
import { Keyboard as RACKeyboard } from "react-aria-components";
import { useShortcut } from "./useShortcut.ts";

// react-aria-components exports the `Keyboard` component but not its `KeyboardProps` type, so derive it.
type RACKeyboardProps = ComponentProps<typeof RACKeyboard>;

export interface ShortcutProps extends Omit<StyledComponentProps<RACKeyboardProps>, "children"> {
    /** A `"+"`-separated descriptor, modifiers first — see `useShortcut`. */
    keys: string;
}

/**
 * The keycaps for a keyboard shortcut, resolved for the current platform and drawn as one `<kbd>`.
 *
 * The hint only: the element the shortcut acts on still needs `aria-keyshortcuts` from the same
 * descriptor, which is what assistive tech reads instead of the keycaps — hence `aria-hidden` here.
 */
export const Shortcut = ({ keys, ref, ...rest }: ShortcutProps) => {
    const { keys: glyphs } = useShortcut(keys);

    const { stylingProps, className, style, ...keyboardProps } = useStyledSystem({
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        verticalAlign: "middle",
        flexShrink: 0,
        minWidth: "core_240",
        height: "core_240",
        paddingX: "inset-xs",
        border: "neutral-weak",
        borderRadius: "rounded-sm",
        backgroundColor: "neutral",
        color: "neutral-weak",
        fontFamily: "body-xs-medium",
        fontSize: "body-xs-medium",
        fontWeight: "body-xs-medium",
        lineHeight: "body-xs-medium",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        ...rest
    });

    const classNames = clsx(stylingProps.className, className);
    const mergedStyles: CSSProperties = {
        boxSizing: "border-box",
        ...stylingProps.style,
        ...style
    };

    return (
        <RACKeyboard data-public aria-hidden ref={ref} className={classNames} style={mergedStyles} {...keyboardProps}>
            {glyphs.join("+")}
        </RACKeyboard>
    );
};
